import React from 'react'
import ToastMessage from '../../../../../../common/ToastMessage';
import { FileIcon } from 'react-file-icon';
import { FieldErrors } from '../../../../../../domain/helpers/Errors';
import { useLanguageStore } from '../../../../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../../../../store/useDarkModeStore';
import { checkIconFile, getFileColor } from '../../../../../../common/GlobalFunctions';
import { QuotationFileInterface } from '../../../../../../models/ProcurementModule/QuotationFile';
import { QuotationRequestInterface } from '../../../../../../models/ProcurementModule/QuotationRequest';

interface UploadedFilesProps {
    hasApprovedRequest: boolean;
    form: QuotationRequestInterface;
    files: QuotationFileInterface[];
    errors: FieldErrors | undefined;
    setForm: React.Dispatch<React.SetStateAction<QuotationRequestInterface>>;
    setFiles: React.Dispatch<React.SetStateAction<QuotationFileInterface[]>>
}

export const UploadedFiles: React.FC<UploadedFilesProps> = ({ hasApprovedRequest, form, files, setForm, setFiles, errors }) => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const handleRemoveFile = async (fileId: string, fileUrl: string, fileName: string) => {
        if (await ToastMessage.Confirm(`Are u sure you want to delete ${fileName}`)) {
            const upFiles = files?.filter(item => item.id !== fileId);

            const upForm = { ...form };
            upForm.files = upFiles;
            upForm.uploadFiles = form.uploadFiles?.filter(item => item !== fileUrl) ?? [];

            setFiles(upFiles);
            setForm(upForm);
        }
    };

    const handleDownload = async (file: QuotationFileInterface) => {
        const link = document.createElement('a');
        link.href = file.fileUrl;
        link.download = file.fileName ?? file.fileUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column gap-2" style={{ width: 400 }}>
            <h5 className="fw-bold">{language['UploadedFiles']}</h5>
            {errors?.['uploadFiles'] && <div className="invalid-feedback-danger ms-2">{errors?.['uploadFiles']}</div>}
            {errors?.['files'] && <div className="invalid-feedback-danger ms-2">{errors?.['files']}</div>}
            <div className='d-flex flex-column gap-2' style={{ maxHeight: 340, overflowY: 'auto' }}>
                {
                    files?.map(item => {
                        const isFileUploaded = !item.progress || item.progress === 100;
                        const extension = item.extension ? item.extension : item.fileName ? item.fileName.split('.').pop() ?? 'file' : 'file';
                        return <div
                            className="card w-100 d-flex flex-row p-4 gap-4 align-items-end position-relative fileIconHover"
                            role='button'
                            onClick={() => handleDownload(item)}
                            key={`file-id-${item.id}`}
                            title={item.fileName}
                            style={{ border: isFileUploaded ? '' : '1px solid red' }}
                        >
                            {!hasApprovedRequest
                                && <span
                                    role='button'
                                    className='showIconHover'
                                    title={`${language['Delete']} ${item.fileName}`}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 10,
                                        color: 'red',
                                        padding: '3px 8px',
                                        borderRadius: '50%',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFile(item.id, item.fileUrl, item.fileName)
                                    }}
                                >
                                    x
                                </span>}

                            <span style={{ width: 25, height: 25 }}>
                                <FileIcon
                                    extension={extension}
                                    color={getFileColor(extension)}
                                    type={checkIconFile(extension)}
                                />
                            </span>
                            <div className="w-100 position-relative">
                                <div className="d-flex flex-row align-items-center justify-content-between mb-1">
                                    <div
                                        className={`d-flex flex-row ${isFileUploaded ? 'text-muted' : darkMode ? 'text-white' : 'text-danger'} gap-1`}
                                        style={{ fontSize: 12 }}
                                    >
                                        <div className='text-truncate' style={{ maxWidth: 100 }}>{item.fileName}</div>
                                        <span> ({item.progress ? item.progress : 100} %)</span>
                                    </div>
                                    <span
                                        className="fw-bold"
                                        style={{ color: isFileUploaded ? 'var(--color-pink)' : 'red', fontSize: 12 }}
                                    >
                                        {isFileUploaded ? language['Completed'] : language['Cancel']}
                                    </span>
                                </div>
                                <div className="progress" style={{ height: 5, borderRadius: 0 }}>
                                    <div
                                        className="progress-bar"
                                        role="progressbar"
                                        aria-valuenow={item.progress === undefined ? 100 : item.progress}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        style={{
                                            width: `${item.progress === undefined ? 100 : item.progress}%`,
                                            background: isFileUploaded ? 'var(--color-pink)' : 'red',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                    )
                }
            </div>
        </div>
    );
}
export default UploadedFiles